// src/pages/Venda3x/index.jsx
// ─── VENDA 3X MAIS — Motor de Lucro com CPA Real ──────────────────────────
import React, { useState, useCallback } from 'react';
import {
  RocketLaunchIcon,
  SparklesIcon,
  BoltIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../lib/apiClient';
import './venda3x.css';

// ── Stepper labels ─────────────────────────────────────────────────────────
const STEPS = [
  { key: 'diagnostico', label: 'Diagnostico' },
  { key: 'criativo',    label: 'Criativo AIDA' },
  { key: 'especialista',label: 'Especialista' },
  { key: 'ads',         label: 'Motor de Vendas' },
  { key: 'whatsapp',    label: 'WhatsApp' },
];

function Stepper({ current }) {
  const idx = STEPS.findIndex(s => s.key === current);
  return (
    <div className="v3x-stepper">
      {STEPS.map((s, i) => (
        <div key={s.key} className={`v3x-step ${i <= idx ? 'v3x-step--done' : ''} ${i === idx ? 'v3x-step--active' : ''}`}>
          <div className="v3x-step__num">{i < idx ? <CheckCircleIcon style={{ width: 16, height: 16 }} /> : i + 1}</div>
          <span className="v3x-step__label">{s.label}</span>
          {i < STEPS.length - 1 && <div className="v3x-step__line" />}
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
export default function Venda3xPage() {
  const [step, setStep]           = useState('diagnostico');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [strategyId, setStrategyId] = useState(null);

  // Passo 1 — Diagnóstico
  const [product, setProduct]         = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [profitPerSale, setProfitPerSale] = useState('');
  const [nicho, setNicho]             = useState('');
  const [diagnosis, setDiagnosis]     = useState(null);

  // Passo 2 — Criativo
  const [creativeType, setCreativeType] = useState('post');
  const [creative, setCreative]       = useState(null);

  // Passo 3 — Especialista
  const [jobId, setJobId]             = useState(null);
  const [jobDone, setJobDone]         = useState(false);

  // Passo 4 — Motor Ads
  const [destino, setDestino]         = useState('');
  const [activated, setActivated]     = useState(false);

  // Passo 5 — WhatsApp
  const [script, setScript]           = useState(null);

  // Performance
  const [perfData, setPerfData]       = useState(null);

  // ── Passo 1: Diagnostico ───────────────────────────────────────────────
  const submitDiagnostico = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await apiClient.request('/mp/3x/start', {
        method: 'POST',
        body: JSON.stringify({
          product,
          product_price:  parseFloat(productPrice) || 0,
          profit_per_sale: parseFloat(profitPerSale) || 0,
          nicho,
        }),
      });
      if (!res?.success) throw new Error(res?.message || 'Erro');
      setStrategyId(res.strategy_id);
      setDiagnosis(res);
      setStep('criativo');
    } catch (e) {
      setError(e.message || 'Erro ao salvar diagnostico.');
    } finally { setLoading(false); }
  }, [product, productPrice, profitPerSale, nicho]);

  // ── Passo 2: Gerar Criativo AIDA ──────────────────────────────────────
  const generateCreative = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await apiClient.request('/mp/3x/generate', {
        method: 'POST',
        body: JSON.stringify({ strategy_id: strategyId, creative_type: creativeType }),
      });
      if (!res?.success) throw new Error(res?.message || 'Erro');
      setCreative(res.creative);
    } catch (e) {
      setError(e.message || 'Erro ao gerar criativo.');
    } finally { setLoading(false); }
  }, [strategyId, creativeType]);

  // ── Passo 3: Solicitar Especialista ────────────────────────────────────
  const requestSpecialist = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await apiClient.request('/mp/3x/refine', {
        method: 'POST',
        body: JSON.stringify({ strategy_id: strategyId }),
      });
      if (!res?.success) throw new Error(res?.message || 'Erro');
      setJobId(res.job_id);
      setJobDone(true);
    } catch (e) {
      setError(e.message || 'Erro ao solicitar especialista.');
    } finally { setLoading(false); }
  }, [strategyId]);

  // ── Passo 4: Ativar Motor de Vendas ───────────────────────────────────
  const activateAds = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await apiClient.request('/mp/3x/activate', {
        method: 'POST',
        body: JSON.stringify({ strategy_id: strategyId, destination: destino }),
      });
      if (!res?.success) throw new Error(res?.message || 'Erro');
      setActivated(true);
      setStep('whatsapp');
    } catch (e) {
      setError(e.message || 'Erro ao ativar. Verifique saldo e destino.');
    } finally { setLoading(false); }
  }, [strategyId, destino]);

  // ── Passo 5: Gerar Script WhatsApp ────────────────────────────────────
  const generateWhatsApp = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await apiClient.request('/mp/3x/whatsapp', {
        method: 'POST',
        body: JSON.stringify({ strategy_id: strategyId }),
      });
      if (!res?.success) throw new Error(res?.message || 'Erro');
      setScript(res.script);
    } catch (e) {
      setError(e.message || 'Erro ao gerar script.');
    } finally { setLoading(false); }
  }, [strategyId]);

  // ── Performance ───────────────────────────────────────────────────────
  const loadPerformance = useCallback(async () => {
    try {
      const res = await apiClient.request(`/mp/3x/performance?strategy_id=${strategyId}`, { method: 'GET' });
      if (res?.success) setPerfData(res);
    } catch {}
  }, [strategyId]);

  const copyText = (text) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="v3x-page">
      <div className="v3x-header">
        <RocketLaunchIcon style={{ width: 28, height: 28, color: '#06B6D4' }} />
        <h1 className="v3x-title">Venda 3x Mais</h1>
      </div>
      <p className="v3x-subtitle">Motor de lucro inteligente em 5 passos — sem promessas irreais.</p>

      <Stepper current={step} />

      {error && (
        <div className="v3x-alert v3x-alert--error">
          <ExclamationTriangleIcon style={{ width: 16, height: 16, flexShrink: 0 }} />
          {error}
        </div>
      )}

      {/* ══ PASSO 1: DIAGNOSTICO ══════════════════════════════════════════ */}
      {step === 'diagnostico' && (
        <div className="v3x-card">
          <h2 className="v3x-card__title">
            <ChartBarIcon style={{ width: 20, height: 20 }} /> Diagnostico Inteligente
          </h2>
          <p className="v3x-card__sub">Informe sobre seu produto para calcularmos o CPA ideal.</p>

          <div className="v3x-form">
            <label className="v3x-label">Produto / Servico *</label>
            <input className="v3x-input" value={product} onChange={e => setProduct(e.target.value)}
              placeholder="Ex: Curso de culinaria, Camiseta premium..." />

            <label className="v3x-label">Preco de Venda (R$) *</label>
            <input className="v3x-input" type="number" min="1" step="0.01"
              value={productPrice} onChange={e => setProductPrice(e.target.value)}
              placeholder="Ex: 100" />

            <label className="v3x-label">Lucro por Venda (R$) *</label>
            <input className="v3x-input" type="number" min="1" step="0.01"
              value={profitPerSale} onChange={e => setProfitPerSale(e.target.value)}
              placeholder="Ex: 40" />

            <label className="v3x-label">Nicho</label>
            <input className="v3x-input" value={nicho} onChange={e => setNicho(e.target.value)}
              placeholder="Ex: restaurantes, moda, educacao" />
          </div>

          <button className="v3x-btn v3x-btn--primary" onClick={submitDiagnostico}
            disabled={loading || !product || !productPrice || !profitPerSale}>
            {loading ? 'Calculando...' : 'Calcular CPA Ideal'}
          </button>
        </div>
      )}

      {/* ══ PASSO 2: CRIATIVO AIDA ═══════════════════════════════════════ */}
      {step === 'criativo' && (
        <div className="v3x-card">
          {/* Diagnóstico resumo */}
          {diagnosis && (
            <div className="v3x-diag-summary">
              <div className="v3x-diag-item">
                <span className="v3x-diag-item__label">CPA Ideal</span>
                <span className="v3x-diag-item__value">R${diagnosis.ideal_cpa}</span>
              </div>
              <div className="v3x-diag-item">
                <span className="v3x-diag-item__label">Orcamento Sugerido</span>
                <span className="v3x-diag-item__value">R${diagnosis.suggested_budget}</span>
              </div>
              <div className="v3x-diag-item">
                <span className="v3x-diag-item__label">Alcance Estimado</span>
                <span className="v3x-diag-item__value">~{(diagnosis.estimated_reach || 0).toLocaleString('pt-BR')} pessoas</span>
              </div>
              <div className="v3x-diag-item">
                <span className="v3x-diag-item__label">Margem</span>
                <span className="v3x-diag-item__value">{diagnosis.margin_pct}%</span>
              </div>
            </div>
          )}

          <p className="v3x-legal-note">{diagnosis?.aviso || 'Estimativa baseada em media de mercado.'}</p>

          <h2 className="v3x-card__title">
            <SparklesIcon style={{ width: 20, height: 20 }} /> Criacao Magnetica (AIDA)
          </h2>
          <p className="v3x-card__sub">Escolha o formato e gere o criativo otimizado para vendas.</p>

          <div className="v3x-type-row">
            {['post', 'carrossel', 'video'].map(t => (
              <button key={t}
                className={`v3x-type-btn ${creativeType === t ? 'v3x-type-btn--active' : ''}`}
                onClick={() => setCreativeType(t)}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          {!creative && (
            <button className="v3x-btn v3x-btn--primary" onClick={generateCreative} disabled={loading}>
              {loading ? 'Gerando com IA...' : 'Gerar Criativo AIDA'}
            </button>
          )}

          {creative && (
            <div className="v3x-creative-result">
              <div className="v3x-creative-headline">"{creative.headline}"</div>

              {creative.copy_aida && (
                <div className="v3x-aida-grid">
                  <div className="v3x-aida-card v3x-aida--atencao">
                    <strong>Atencao</strong><p>{creative.copy_aida.atencao}</p>
                  </div>
                  <div className="v3x-aida-card v3x-aida--interesse">
                    <strong>Interesse</strong><p>{creative.copy_aida.interesse}</p>
                  </div>
                  <div className="v3x-aida-card v3x-aida--desejo">
                    <strong>Desejo</strong><p>{creative.copy_aida.desejo}</p>
                  </div>
                  <div className="v3x-aida-card v3x-aida--acao">
                    <strong>Acao</strong><p>{creative.copy_aida.acao}</p>
                  </div>
                </div>
              )}

              {creative.legenda_completa && (
                <div className="v3x-legenda-box">
                  <div className="v3x-legenda-box__header">
                    <span>Legenda Completa</span>
                    <button className="v3x-btn v3x-btn--ghost v3x-btn--sm" onClick={() => copyText(creative.legenda_completa)}>
                      <ClipboardDocumentIcon style={{ width: 14, height: 14 }} /> Copiar
                    </button>
                  </div>
                  <p>{creative.legenda_completa}</p>
                </div>
              )}

              {creative.sugestao_visual && (
                <div className="v3x-visual-hint">
                  <strong>Sugestao Visual:</strong> {creative.sugestao_visual}
                </div>
              )}

              <div className="v3x-btn-row">
                <button className="v3x-btn v3x-btn--ghost" onClick={generateCreative} disabled={loading}>
                  Regerar
                </button>
                <button className="v3x-btn v3x-btn--primary" onClick={() => setStep('especialista')}>
                  Proximo: Especialista
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ PASSO 3: ESPECIALISTA ════════════════════════════════════════ */}
      {step === 'especialista' && (
        <div className="v3x-card">
          <h2 className="v3x-card__title">
            <SparklesIcon style={{ width: 20, height: 20 }} /> Refino por Especialista
          </h2>
          <p className="v3x-card__sub">Um especialista ajusta a copy emocional do seu criativo. Custo: 3 creditos (escrow).</p>

          {!jobDone ? (
            <>
              <div className="v3x-escrow-info">
                <ExclamationTriangleIcon style={{ width: 16, height: 16 }} />
                3 creditos serao travados (escrow) e liberados ao especialista apos entrega.
              </div>
              <div className="v3x-btn-row">
                <button className="v3x-btn v3x-btn--specialist" onClick={requestSpecialist} disabled={loading}>
                  {loading ? 'Solicitando...' : 'Refinar com Especialista (3 cr)'}
                </button>
                <button className="v3x-btn v3x-btn--ghost" onClick={() => setStep('ads')}>
                  Pular — ir para Ads
                </button>
              </div>
            </>
          ) : (
            <div className="v3x-job-done">
              <CheckCircleIcon style={{ width: 24, height: 24, color: '#4ade80' }} />
              <div>
                <p><strong>Especialista acionado!</strong></p>
                <p className="v3x-job-done__id">Job: {jobId}</p>
              </div>
              <button className="v3x-btn v3x-btn--primary" onClick={() => setStep('ads')}>
                Proximo: Motor de Vendas
              </button>
            </div>
          )}
        </div>
      )}

      {/* ══ PASSO 4: MOTOR DE VENDAS (ADS) ══════════════════════════════ */}
      {step === 'ads' && (
        <div className="v3x-card">
          <h2 className="v3x-card__title">
            <BoltIcon style={{ width: 20, height: 20 }} /> Motor de Vendas
          </h2>
          <p className="v3x-card__sub">Configure o destino e ative seus anuncios.</p>

          {diagnosis && (
            <div className="v3x-ads-config">
              <div className="v3x-ads-row">
                <span>Orcamento sugerido:</span>
                <strong>R${diagnosis.suggested_budget}</strong>
              </div>
              <div className="v3x-ads-row">
                <span>Publico sugerido:</span>
                <strong>{diagnosis.audience?.age} · Raio {diagnosis.audience?.radius_km}km</strong>
              </div>
              <div className="v3x-ads-row">
                <span>Melhores dias:</span>
                <strong>{diagnosis.schedule?.best_days?.join(', ')}</strong>
              </div>
              <div className="v3x-ads-row">
                <span>Melhor horario:</span>
                <strong>{diagnosis.schedule?.best_hours?.join(', ')}</strong>
              </div>
            </div>
          )}

          <label className="v3x-label">Link de Destino (WhatsApp ou Instagram) *</label>
          <input className="v3x-input" value={destino} onChange={e => setDestino(e.target.value)}
            placeholder="https://wa.me/5511... ou https://instagram.com/..." />

          {!activated ? (
            <button className="v3x-btn v3x-btn--primary" onClick={activateAds}
              disabled={loading || !destino}>
              {loading ? 'Ativando...' : 'Ativar Motor de Vendas'}
            </button>
          ) : (
            <div className="v3x-activated">
              <CheckCircleIcon style={{ width: 24, height: 24, color: '#4ade80' }} />
              <span>Motor de vendas ativado!</span>
            </div>
          )}
        </div>
      )}

      {/* ══ PASSO 5: WHATSAPP SCRIPT ════════════════════════════════════ */}
      {step === 'whatsapp' && (
        <div className="v3x-card">
          <h2 className="v3x-card__title">
            <ChatBubbleLeftRightIcon style={{ width: 20, height: 20 }} /> Script de Fechamento WhatsApp
          </h2>
          <p className="v3x-card__sub">Script automatico para converter visitantes em clientes.</p>

          {!script ? (
            <button className="v3x-btn v3x-btn--primary" onClick={generateWhatsApp} disabled={loading}>
              {loading ? 'Gerando script...' : 'Gerar Script de Fechamento'}
            </button>
          ) : (
            <div className="v3x-script-result">
              {['abertura', 'qualificacao', 'oferta', 'fechamento'].map(key => (
                <div key={key} className="v3x-script-block">
                  <div className="v3x-script-block__header">
                    <strong>{key.charAt(0).toUpperCase() + key.slice(1)}</strong>
                    <button className="v3x-btn v3x-btn--ghost v3x-btn--sm" onClick={() => copyText(script[key])}>
                      <ClipboardDocumentIcon style={{ width: 14, height: 14 }} /> Copiar
                    </button>
                  </div>
                  <p className="v3x-script-block__text">{script[key]}</p>
                </div>
              ))}

              <div className="v3x-btn-row" style={{ marginTop: '1rem' }}>
                <button className="v3x-btn v3x-btn--ghost" onClick={generateWhatsApp} disabled={loading}>
                  Regerar Script
                </button>
                <button className="v3x-btn v3x-btn--primary" onClick={() => { setStep('performance'); loadPerformance(); }}>
                  Ver Performance
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ PERFORMANCE + ALERTAS ══════════════════════════════════════════ */}
      {step === 'performance' && (
        <div className="v3x-card">
          <h2 className="v3x-card__title">
            <ChartBarIcon style={{ width: 20, height: 20 }} /> Monitoramento + Alertas
          </h2>

          <button className="v3x-btn v3x-btn--ghost v3x-btn--sm" onClick={loadPerformance} style={{ marginBottom: '1rem' }}>
            Atualizar dados
          </button>

          {perfData?.new_alert && (
            <div className="v3x-alert v3x-alert--warning">
              <ExclamationTriangleIcon style={{ width: 16, height: 16, flexShrink: 0 }} />
              {perfData.new_alert.message}
            </div>
          )}

          {perfData?.metrics && (
            <div className="v3x-perf-grid">
              <div className="v3x-perf-card">
                <div className="v3x-perf-card__val">{perfData.metrics.impressions}</div>
                <div className="v3x-perf-card__label">Impressoes</div>
              </div>
              <div className="v3x-perf-card">
                <div className="v3x-perf-card__val">{perfData.metrics.clicks}</div>
                <div className="v3x-perf-card__label">Cliques</div>
              </div>
              <div className="v3x-perf-card">
                <div className="v3x-perf-card__val">R${perfData.metrics.spend}</div>
                <div className="v3x-perf-card__label">Investido</div>
              </div>
              <div className="v3x-perf-card">
                <div className="v3x-perf-card__val">{perfData.metrics.conversions}</div>
                <div className="v3x-perf-card__label">Conversoes</div>
              </div>
              <div className="v3x-perf-card">
                <div className="v3x-perf-card__val">R${perfData.ideal_cpa}</div>
                <div className="v3x-perf-card__label">CPA Ideal</div>
              </div>
              <div className="v3x-perf-card">
                <div className="v3x-perf-card__val">R${perfData.metrics.cpa_real}</div>
                <div className="v3x-perf-card__label">CPA Real</div>
              </div>
            </div>
          )}

          {perfData?.alerts?.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <h3 className="v3x-card__sub">Alertas Ativos</h3>
              {perfData.alerts.map((a, i) => (
                <div key={i} className="v3x-alert v3x-alert--warning" style={{ marginTop: '0.5rem' }}>
                  <ExclamationTriangleIcon style={{ width: 14, height: 14, flexShrink: 0 }} />
                  {a.message}
                </div>
              ))}
            </div>
          )}

          <p className="v3x-legal-note" style={{ marginTop: '1rem' }}>
            Dados de performance sao atualizados via integracao com plataforma de anuncios. Nenhuma promessa de resultados e feita.
          </p>
        </div>
      )}
    </div>
  );
}
